import React, { useState, useMemo } from 'react';
import {
  DataGrid as MuiDataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import { Box, Typography, IconButton, TextField, InputAdornment, Chip, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { LoadingSpinner } from '../index';

const StyledDataGrid = styled(MuiDataGrid)(({ theme }) => ({
  border: 'none',
  '& .MuiDataGrid-root': {
    border: 'none',
  },
  '& .MuiDataGrid-main': {
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: theme.palette.grey[50],
    borderBottom: `2px solid ${theme.palette.divider}`,
    '& .MuiDataGrid-columnHeader': {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:focus': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-row': {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected': {
      backgroundColor: `${theme.palette.primary.main}10`,
      '&:hover': {
        backgroundColor: `${theme.palette.primary.main}20`,
      },
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
}));

// Custom toolbar component
function CustomToolbar({
  title,
  showSearch = true,
  searchValue,
  onSearchChange,
  additionalActions,
  rowCount = 0,
}) {
  return (
    <GridToolbarContainer
      sx={{
        p: 2,
        justifyContent: 'space-between',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {title && (
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="textSecondary">
          {rowCount} record{rowCount !== 1 ? 's' : ''}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showSearch && (
          <TextField
            size="small"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />
        )}

        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />

        {additionalActions}
      </Box>
    </GridToolbarContainer>
  );
}

/**
 * Enhanced MUI DataGrid component with additional features
 *
 * @param {Object} props - Component props
 * @param {Array} props.rows - Grid data rows
 * @param {Array} props.columns - Grid column definitions
 * @param {boolean} props.loading - Loading state
 * @param {string} props.title - Grid title
 * @param {boolean} props.showSearch - Show search functionality
 * @param {function} props.onRowClick - Row click handler
 * @param {function} props.onEdit - Edit action handler
 * @param {function} props.onDelete - Delete action handler
 * @param {function} props.onView - View action handler
 * @param {boolean} props.checkboxSelection - Enable checkbox selection
 * @param {boolean} props.disableRowSelectionOnClick - Disable row selection on click
 * @param {Array} props.actions - Custom actions for each row
 * @param {React.ReactNode} props.additionalActions - Additional toolbar actions
 * @param {string} props.error - Error message
 * @param {Object} props.paginationModel - Pagination configuration
 * @param {function} props.onPaginationModelChange - Pagination change handler
 */
const DataGrid = ({
  rows,
  columns,
  loading = false,
  title,
  showSearch = true,
  onRowClick,
  onEdit,
  onDelete,
  onView,
  checkboxSelection = false,
  disableRowSelectionOnClick = false,
  actions = [],
  additionalActions,
  error,
  paginationModel = { page: 0, pageSize: 25 },
  onPaginationModelChange,
  ...otherProps
}) => {
  const [searchValue, setSearchValue] = useState('');

  // Filter rows based on search
  const filteredRows = useMemo(() => {
    if (!searchValue.trim()) return rows;

    const searchLower = searchValue.toLowerCase();
    return rows.filter((row) =>
      Object.values(row).some((value) => String(value).toLowerCase().includes(searchLower))
    );
  }, [rows, searchValue]);

  // Enhanced columns with actions
  const enhancedColumns = useMemo(() => {
    const baseColumns = [...columns];

    // Add actions column if any actions are provided
    const hasActions = onEdit || onDelete || onView || actions.length > 0;

    if (hasActions) {
      const actionItems = [];

      if (onView) {
        actionItems.push(
          <GridActionsCellItem
            icon={<VisibilityIcon />}
            label="View"
            onClick={(params) => onView(params.row)}
            key="view"
          />
        );
      }

      if (onEdit) {
        actionItems.push(
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={(params) => onEdit(params.row)}
            key="edit"
          />
        );
      }

      if (onDelete) {
        actionItems.push(
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={(params) => onDelete(params.row)}
            key="delete"
            showInMenu
          />
        );
      }

      // Add custom actions
      actions.forEach((action, index) => {
        actionItems.push(<GridActionsCellItem {...action} key={action.key || `action-${index}`} />);
      });

      baseColumns.push({
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 120,
        getActions: () => actionItems,
      });
    }

    return baseColumns;
  }, [columns, onEdit, onDelete, onView, actions]);

  // Handle search
  const handleSearchChange = (value) => {
    setSearchValue(value);
  };

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Empty state
  if (!loading && filteredRows.length === 0 && !searchValue) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          No data available
        </Typography>
        <Typography variant="body2" color="textSecondary">
          There are no records to display.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 300px)', minHeight: 400, width: '100%' }}>
      <StyledDataGrid
        rows={filteredRows}
        columns={enhancedColumns}
        loading={loading}
        pageSizeOptions={[25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        onRowClick={onRowClick}
        slots={{
          toolbar: CustomToolbar,
          loadingOverlay: LoadingSpinner,
          noRowsOverlay: () => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography variant="h6" color="textSecondary">
                {searchValue ? 'No results found' : 'No data available'}
              </Typography>
              {searchValue && (
                <Typography variant="body2" color="textSecondary">
                  Try adjusting your search terms
                </Typography>
              )}
            </Box>
          ),
        }}
        slotProps={{
          toolbar: {
            title,
            showSearch,
            searchValue,
            onSearchChange: handleSearchChange,
            additionalActions,
            rowCount: filteredRows.length,
          },
        }}
        {...otherProps}
      />
    </Box>
  );
};

DataGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  title: PropTypes.string,
  showSearch: PropTypes.bool,
  onRowClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onView: PropTypes.func,
  checkboxSelection: PropTypes.bool,
  disableRowSelectionOnClick: PropTypes.bool,
  actions: PropTypes.array,
  additionalActions: PropTypes.node,
  error: PropTypes.string,
  paginationModel: PropTypes.object,
  onPaginationModelChange: PropTypes.func,
};

export default DataGrid;
